<template>
  <div class="echipa-wrap">
    <!-- Filtru -->
    <v-card elevation="1" class="filter-card mb-4">
      <div class="filter-row">
        <div class="filter-group">
          <v-icon size="18" class="mr-1" color="grey">mdi-calendar-month</v-icon>
          <v-select v-model="luna" :items="luni" item-title="label" item-value="value" density="compact"
            variant="outlined" hide-details class="filter-select" />
          <v-select v-model="an" :items="ani" density="compact" variant="outlined" hide-details
            class="filter-select ml-2" />
        </div>
        <v-btn :color="APP_Color" size="small" class="refresh-btn" :loading="loading" @click="incarca">
          <v-icon size="16" class="mr-1">mdi-refresh</v-icon> Încarcă
        </v-btn>
      </div>
    </v-card>

    <!-- Loading -->
    <div v-if="loading" class="d-flex justify-center py-8">
      <v-progress-circular indeterminate :color="APP_Color" size="40" />
    </div>

    <v-alert v-else-if="!loading && echipaData.length === 0 && loaded" type="info" variant="tonal">
      Nu există agenți cu rol activ în această perioadă.
    </v-alert>

    <div v-else-if="!loading && echipaData.length > 0">

      <!-- ── Rezumat rapid ── -->
      <v-row class="mb-4" dense>
        <v-col cols="12" sm="4">
          <v-card class="summary-card" elevation="1">
            <div class="summary-inner">
              <div class="summary-icon" style="background:rgba(255,87,34,0.1)">
                <v-icon color="deep-orange" size="22">mdi-account-search</v-icon>
              </div>
              <div>
                <div class="summary-value">{{ hunterAgenti.length }}</div>
                <div class="summary-label">Agenți Hunter</div>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card class="summary-card" elevation="1">
            <div class="summary-inner">
              <div class="summary-icon" style="background:rgba(76,175,80,0.1)">
                <v-icon color="green" size="22">mdi-account-tie</v-icon>
              </div>
              <div>
                <div class="summary-value">{{ farmerAgenti.length }}</div>
                <div class="summary-label">Agenți Farmer</div>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card class="summary-card" elevation="1">
            <div class="summary-inner">
              <div class="summary-icon" :style="{ background: `rgba(${medieEchipaRGB},0.1)` }">
                <v-icon :color="medieEchipaColor" size="22">mdi-chart-donut</v-icon>
              </div>
              <div>
                <div class="summary-value" :style="{ color: medieEchipaColor }">{{ medieEchipa }}%</div>
                <div class="summary-label">Medie echipă</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- ── Tabel Hunter ── -->
      <v-card v-if="hunterAgenti.length > 0" elevation="2" class="matrix-card mb-4">
        <div class="matrix-title">
          <v-icon color="deep-orange" size="18" class="mr-1">mdi-account-search</v-icon>
          Hunter — Achiziție clienți noi
          <span class="matrix-hint ml-2">click pe agent pentru detalii</span>
        </div>
        <div class="table-scroll">
          <table class="matrix-table">
            <thead>
              <tr>
                <th class="agent-col">Agent</th>
                <th v-for="col in hunterColumns" :key="col.ID" class="kpi-col">
                  <v-tooltip :text="col.Denumire" location="top">
                    <template #activator="{ props }">
                      <span v-bind="props">{{ abrev(col.Cod) }}</span>
                    </template>
                  </v-tooltip>
                </th>
                <th class="medie-col">Medie %</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="member in hunterAgenti" :key="member.Utilizator">
                <tr class="agent-row"
                    :class="{ 'agent-row--expanded': expandedAgent === member.Utilizator }"
                    @click="toggleAgent(member)">
                  <td class="agent-col">
                    <div class="agent-name-wrap">
                      <v-icon size="14" class="expand-chevron mr-1"
                        :class="{ 'expand-chevron--open': expandedAgent === member.Utilizator }"
                        color="grey">mdi-chevron-right</v-icon>
                      <span class="agent-name">{{ member.Utilizator }}</span>
                    </div>
                  </td>
                  <td v-for="col in hunterColumns" :key="col.ID" class="kpi-col">
                    <template v-if="getKPI(member, col.ID)">
                      <div class="cell-wrap" :style="{ color: kpiColor(getKPI(member, col.ID).ProcentRealizat) }">
                        <strong>{{ getKPI(member, col.ID).Realizat }}</strong>
                        <span class="cell-target">/{{ getKPI(member, col.ID).Target }}</span>
                      </div>
                      <div class="cell-bar-wrap">
                        <div class="cell-bar">
                          <div class="cell-fill"
                            :style="{ width: Math.min(getKPI(member, col.ID).ProcentRealizat, 100) + '%',
                                      background: kpiColor(getKPI(member, col.ID).ProcentRealizat) }">
                          </div>
                        </div>
                        <span class="cell-pct" :style="{ color: kpiColor(getKPI(member, col.ID).ProcentRealizat) }">
                          {{ getKPI(member, col.ID).ProcentRealizat }}%
                        </span>
                      </div>
                    </template>
                    <span v-else class="text-grey">—</span>
                  </td>
                  <td class="medie-col">
                    <v-chip :color="kpiChipColor(medieMember(member.KPIs))" size="small" label>
                      {{ medieMember(member.KPIs) }}%
                    </v-chip>
                  </td>
                </tr>

                <!-- Rând expand -->
                <tr v-if="expandedAgent === member.Utilizator" class="detalii-row">
                  <td :colspan="hunterColumns.length + 2" class="detalii-cell">
                    <div class="detalii-content">
                      <!-- Loading detalii agent -->
                      <div v-if="loadingAgentDetalii[member.Utilizator]" class="d-flex justify-center py-4">
                        <v-progress-circular indeterminate :color="APP_Color" size="30" />
                      </div>
                      <!-- Grid KPI blocks -->
                      <div v-else class="kpi-detalii-grid">
                        <div v-for="kpi in member.KPIs" :key="kpi.KPIDefinitieID" class="kpi-detalii-block">
                          <!-- Header bloc KPI -->
                          <div class="kpi-block-header">
                            <span class="kpi-block-title">{{ kpi.Denumire }}</span>
                            <div class="kpi-block-progress-row">
                              <span :style="{ color: kpiColor(kpi.ProcentRealizat), fontWeight: '700', fontSize: '0.8rem' }">
                                {{ kpi.Realizat }}/{{ kpi.Target }}
                              </span>
                              <span class="kpi-block-pct" :style="{ color: kpiColor(kpi.ProcentRealizat) }">
                                {{ kpi.ProcentRealizat }}%
                              </span>
                            </div>
                            <div class="kpi-block-bar">
                              <div class="kpi-block-fill"
                                :style="{ width: Math.min(kpi.ProcentRealizat, 100) + '%', background: kpiColor(kpi.ProcentRealizat) }">
                              </div>
                            </div>
                          </div>
                          <!-- Body bloc KPI -->
                          <div class="kpi-block-body">
                            <template v-if="agentDetalii[member.Utilizator] && agentDetalii[member.Utilizator][kpi.KPIDefinitieID] !== undefined">
                              <div v-if="agentDetalii[member.Utilizator][kpi.KPIDefinitieID].length === 0" class="kpi-block-empty">
                                Nicio înregistrare
                              </div>
                              <div v-else class="mini-scroll">
                                <table class="mini-table">
                                  <thead>
                                    <tr>
                                      <th>{{ labelColoana(getSursaDate(kpi)) }}</th>
                                      <th v-if="areClient(getSursaDate(kpi))">Client</th>
                                      <th v-if="areContact(getSursaDate(kpi))">{{ labelContact(getSursaDate(kpi)) }}</th>
                                      <th v-if="areValoare(getSursaDate(kpi))">Valoare</th>
                                      <th v-if="areStatus(getSursaDate(kpi))">Status</th>
                                      <th>Data</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="row in agentDetalii[member.Utilizator][kpi.KPIDefinitieID]" :key="row.ID"
                                        class="mini-row-link"
                                        @click="navigheazaLaDetaliu(getSursaDate(kpi), row.ID)">
                                      <td class="mini-col-main">{{ row.Denumire }}</td>
                                      <td v-if="areClient(getSursaDate(kpi))" class="mini-col-client">{{ row.Client || '—' }}</td>
                                      <td v-if="areContact(getSursaDate(kpi))" class="mini-col-info">{{ row.Contact || '—' }}</td>
                                      <td v-if="areValoare(getSursaDate(kpi))" class="mini-col-val">
                                        {{ row.Valoare != null ? formatValoare(row.Valoare) : '—' }}
                                      </td>
                                      <td v-if="areStatus(getSursaDate(kpi))">
                                        <v-chip v-if="row.Status" size="x-small" variant="tonal" color="blue">{{ row.Status }}</v-chip>
                                        <span v-else>—</span>
                                      </td>
                                      <td class="mini-col-data">{{ formatData(row.Data) }}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </template>
                            <div v-else class="kpi-block-empty">
                              <v-progress-circular indeterminate size="14" width="2" color="grey" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </v-card>

      <!-- ── Tabel Farmer ── -->
      <v-card v-if="farmerAgenti.length > 0" elevation="2" class="matrix-card">
        <div class="matrix-title">
          <v-icon color="green" size="18" class="mr-1">mdi-account-tie</v-icon>
          Farmer — Gestionare portofoliu
          <span class="matrix-hint ml-2">click pe agent pentru detalii</span>
        </div>
        <div class="table-scroll">
          <table class="matrix-table">
            <thead>
              <tr>
                <th class="agent-col">Agent</th>
                <th v-for="col in farmerColumns" :key="col.ID" class="kpi-col">
                  <v-tooltip :text="col.Denumire" location="top">
                    <template #activator="{ props }">
                      <span v-bind="props">{{ abrev(col.Cod) }}</span>
                    </template>
                  </v-tooltip>
                </th>
                <th class="medie-col">Medie %</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="member in farmerAgenti" :key="member.Utilizator">
                <tr class="agent-row"
                    :class="{ 'agent-row--expanded': expandedAgent === member.Utilizator }"
                    @click="toggleAgent(member)">
                  <td class="agent-col">
                    <div class="agent-name-wrap">
                      <v-icon size="14" class="expand-chevron mr-1"
                        :class="{ 'expand-chevron--open': expandedAgent === member.Utilizator }"
                        color="grey">mdi-chevron-right</v-icon>
                      <span class="agent-name">{{ member.Utilizator }}</span>
                    </div>
                  </td>
                  <td v-for="col in farmerColumns" :key="col.ID" class="kpi-col">
                    <template v-if="getKPI(member, col.ID)">
                      <div class="cell-wrap" :style="{ color: kpiColor(getKPI(member, col.ID).ProcentRealizat) }">
                        <strong>{{ getKPI(member, col.ID).Realizat }}</strong>
                        <span class="cell-target">/{{ getKPI(member, col.ID).Target }}</span>
                      </div>
                      <div class="cell-bar-wrap">
                        <div class="cell-bar">
                          <div class="cell-fill"
                            :style="{ width: Math.min(getKPI(member, col.ID).ProcentRealizat, 100) + '%',
                                      background: kpiColor(getKPI(member, col.ID).ProcentRealizat) }">
                          </div>
                        </div>
                        <span class="cell-pct" :style="{ color: kpiColor(getKPI(member, col.ID).ProcentRealizat) }">
                          {{ getKPI(member, col.ID).ProcentRealizat }}%
                        </span>
                      </div>
                    </template>
                    <span v-else class="text-grey">—</span>
                  </td>
                  <td class="medie-col">
                    <v-chip :color="kpiChipColor(medieMember(member.KPIs))" size="small" label>
                      {{ medieMember(member.KPIs) }}%
                    </v-chip>
                  </td>
                </tr>

                <!-- Rând expand -->
                <tr v-if="expandedAgent === member.Utilizator" class="detalii-row">
                  <td :colspan="farmerColumns.length + 2" class="detalii-cell">
                    <div class="detalii-content">
                      <div v-if="loadingAgentDetalii[member.Utilizator]" class="d-flex justify-center py-4">
                        <v-progress-circular indeterminate :color="APP_Color" size="30" />
                      </div>
                      <div v-else class="kpi-detalii-grid">
                        <div v-for="kpi in member.KPIs" :key="kpi.KPIDefinitieID" class="kpi-detalii-block">
                          <div class="kpi-block-header">
                            <span class="kpi-block-title">{{ kpi.Denumire }}</span>
                            <div class="kpi-block-progress-row">
                              <span :style="{ color: kpiColor(kpi.ProcentRealizat), fontWeight: '700', fontSize: '0.8rem' }">
                                {{ kpi.Realizat }}/{{ kpi.Target }}
                              </span>
                              <span class="kpi-block-pct" :style="{ color: kpiColor(kpi.ProcentRealizat) }">
                                {{ kpi.ProcentRealizat }}%
                              </span>
                            </div>
                            <div class="kpi-block-bar">
                              <div class="kpi-block-fill"
                                :style="{ width: Math.min(kpi.ProcentRealizat, 100) + '%', background: kpiColor(kpi.ProcentRealizat) }">
                              </div>
                            </div>
                          </div>
                          <div class="kpi-block-body">
                            <template v-if="agentDetalii[member.Utilizator] && agentDetalii[member.Utilizator][kpi.KPIDefinitieID] !== undefined">
                              <div v-if="agentDetalii[member.Utilizator][kpi.KPIDefinitieID].length === 0" class="kpi-block-empty">
                                Nicio înregistrare
                              </div>
                              <div v-else class="mini-scroll">
                                <table class="mini-table">
                                  <thead>
                                    <tr>
                                      <th>{{ labelColoana(getSursaDate(kpi)) }}</th>
                                      <th v-if="areClient(getSursaDate(kpi))">Client</th>
                                      <th v-if="areContact(getSursaDate(kpi))">{{ labelContact(getSursaDate(kpi)) }}</th>
                                      <th v-if="areValoare(getSursaDate(kpi))">Valoare</th>
                                      <th v-if="areStatus(getSursaDate(kpi))">Status</th>
                                      <th>Data</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="row in agentDetalii[member.Utilizator][kpi.KPIDefinitieID]" :key="row.ID"
                                        class="mini-row-link"
                                        @click="navigheazaLaDetaliu(getSursaDate(kpi), row.ID)">
                                      <td class="mini-col-main">{{ row.Denumire }}</td>
                                      <td v-if="areClient(getSursaDate(kpi))" class="mini-col-client">{{ row.Client || '—' }}</td>
                                      <td v-if="areContact(getSursaDate(kpi))" class="mini-col-info">{{ row.Contact || '—' }}</td>
                                      <td v-if="areValoare(getSursaDate(kpi))" class="mini-col-val">
                                        {{ row.Valoare != null ? formatValoare(row.Valoare) : '—' }}
                                      </td>
                                      <td v-if="areStatus(getSursaDate(kpi))">
                                        <v-chip v-if="row.Status" size="x-small" variant="tonal" color="blue">{{ row.Status }}</v-chip>
                                        <span v-else>—</span>
                                      </td>
                                      <td class="mini-col-data">{{ formatData(row.Data) }}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </template>
                            <div v-else class="kpi-block-empty">
                              <v-progress-circular indeterminate size="14" width="2" color="grey" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </v-card>

    </div>

    <!-- Starea inițială -->
    <div v-else-if="!loading && !loaded" class="text-center text-grey py-8">
      <v-icon size="40" color="grey-lighten-2">mdi-chart-bar</v-icon>
      <div class="mt-2">Selectați perioada și apăsați „Încarcă".</div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import AppSettings from '@/settings/appSettings'
import CrmKPIService from '@/services/Comercial/CrmKPI'

const COD_ABREV: Record<string, string> = {
  HUNTER_COMPANII_NOI:            'Comp. Noi',
  HUNTER_CONTACTE_NOI:            'Contacte',
  HUNTER_INTALNIRI:               'Întâlniri',
  HUNTER_DEMO:                    'Demo',
  HUNTER_OPORTUNITATI_NOI:        'Oport.',
  FARMER_CLIENTI_CONTACTATI:      'Clienți',
  FARMER_INTALNIRI_PORTOFOLIU:    'Întâlniri',
  FARMER_UPSELL_CROSSSELL:        'Upsell',
  FARMER_OPORTUNITATI_PORTOFOLIU: 'Oport.',
  FARMER_OFERTE_TRIMISE:          'Oferte',
}

export default defineComponent({
  name: 'GridKPIEchipa',

  emits: ['selectAgent'],

  data() {
    return {
      APP_Color: AppSettings.APP_Color,
      loading: false,
      loaded: false,
      echipaData: [] as any[],

      an: new Date().getFullYear(),
      luna: new Date().getMonth() + 1,

      ani: Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i),
      luni: [
        { label: 'Ianuarie', value: 1 },   { label: 'Februarie', value: 2 },
        { label: 'Martie', value: 3 },     { label: 'Aprilie', value: 4 },
        { label: 'Mai', value: 5 },        { label: 'Iunie', value: 6 },
        { label: 'Iulie', value: 7 },      { label: 'August', value: 8 },
        { label: 'Septembrie', value: 9 }, { label: 'Octombrie', value: 10 },
        { label: 'Noiembrie', value: 11 }, { label: 'Decembrie', value: 12 },
      ],

      expandedAgent: null as string | null,
      // agentDetalii[utilizator][kpiDefinitieID] = CRM_KPIDetaliuItem[]
      agentDetalii: {} as Record<string, Record<string, any[]>>,
      loadingAgentDetalii: {} as Record<string, boolean>,
    }
  },

  computed: {
    hunterAgenti(): any[] {
      return this.echipaData.filter(m => m.TipAgent === 'Hunter')
    },
    farmerAgenti(): any[] {
      return this.echipaData.filter(m => m.TipAgent === 'Farmer')
    },
    allColumns(): any[] {
      const map = new Map<string, any>()
      for (const member of this.echipaData) {
        for (const kpi of member.KPIs || []) {
          if (!map.has(kpi.KPIDefinitieID)) {
            map.set(kpi.KPIDefinitieID, {
              ID: kpi.KPIDefinitieID,
              Denumire: kpi.Denumire,
              TipAgent: kpi.TipAgent,
              Cod: kpi.Cod,
            })
          }
        }
      }
      return Array.from(map.values())
    },
    hunterColumns(): any[] {
      return this.allColumns.filter(c => c.TipAgent === 'Hunter' || c.TipAgent === 'Ambele')
    },
    farmerColumns(): any[] {
      return this.allColumns.filter(c => c.TipAgent === 'Farmer' || c.TipAgent === 'Ambele')
    },
    medieEchipa(): number {
      if (!this.echipaData.length) return 0
      const toate = this.echipaData.map(m => this.medieMember(m.KPIs))
      return Math.round(toate.reduce((a, b) => a + b, 0) / toate.length)
    },
    medieEchipaColor(): string { return this.kpiColor(this.medieEchipa) },
    medieEchipaRGB(): string {
      const m = this.medieEchipa
      if (m >= 100) return '76,175,80'
      if (m >= 70) return '255,152,0'
      return '244,67,54'
    },
  },

  methods: {
    async incarca() {
      this.loading = true
      this.loaded = false
      this.expandedAgent = null
      this.agentDetalii = {}
      try {
        const res = await CrmKPIService.getEchipaRaport(this.an, this.luna)
        this.echipaData = res.data || []
        this.loaded = true
      } catch (e) {
        console.error(e)
        this.echipaData = []
        this.loaded = true
      } finally {
        this.loading = false
      }
    },

    async toggleAgent(member: any) {
      if (this.expandedAgent === member.Utilizator) {
        this.expandedAgent = null
        return
      }

      this.expandedAgent = member.Utilizator

      // Date deja încărcate — nu refacem request-ul
      if (this.agentDetalii[member.Utilizator]) return

      this.loadingAgentDetalii = { ...this.loadingAgentDetalii, [member.Utilizator]: true }

      try {
        const results: Record<string, any[]> = {}

        await Promise.all(
          (member.KPIs || []).map(async (kpi: any) => {
            const sursaDate = this.getSursaDate(kpi)
            if (!sursaDate) return
            try {
              const res = await CrmKPIService.getDetaliu(member.Utilizator, this.an, this.luna, sursaDate)
              results[kpi.KPIDefinitieID] = res.data || []
            } catch {
              results[kpi.KPIDefinitieID] = []
            }
          })
        )

        // Înlocuim întregul obiect pentru a declanșa reactivitatea Vue
        this.agentDetalii = { ...this.agentDetalii, [member.Utilizator]: { ...results } }
      } catch (e) {
        console.error(e)
        this.agentDetalii = { ...this.agentDetalii, [member.Utilizator]: {} }
      } finally {
        this.loadingAgentDetalii = { ...this.loadingAgentDetalii, [member.Utilizator]: false }
      }
    },

    getSursaDate(kpi: any): string {
      if (kpi.SursaDate) return kpi.SursaDate
      const cod: string = kpi.Cod || ''
      const idx = cod.indexOf('_')
      return idx > -1 ? cod.substring(idx + 1) : cod
    },

    getKPI(member: any, kpiID: string): any {
      return member.KPIs?.find((k: any) => k.KPIDefinitieID === kpiID)
    },

    medieMember(kpis: any[]): number {
      if (!kpis?.length) return 0
      return Math.round(kpis.reduce((a: number, k: any) => a + (k.ProcentRealizat || 0), 0) / kpis.length)
    },

    kpiColor(p: number): string {
      if (p >= 100) return '#4CAF50'
      if (p >= 70) return '#FF9800'
      return '#F44336'
    },

    kpiChipColor(p: number): string {
      if (p >= 100) return 'success'
      if (p >= 70) return 'warning'
      return 'error'
    },

    abrev(cod: string): string {
      return COD_ABREV[cod] || cod.split('_').slice(1).join(' ')
    },

    // ── Helpers coloane detalii ──────────────────────────────────────────────

    labelColoana(s: string): string {
      const m: Record<string, string> = {
        COMPANII_NOI: 'Companie', CONTACTE_NOI: 'Contact',
        INTALNIRI: 'Subiect', DEMO: 'Subiect',
        OPORTUNITATI_NOI: 'Oportunitate', CLIENTI_CONTACTATI: 'Client',
        INTALNIRI_PORTOFOLIU: 'Subiect', UPSELL_CROSSSELL: 'Subiect',
        OPORTUNITATI_PORTOFOLIU: 'Oportunitate', OFERTE_TRIMISE: 'Ofertă',
      }
      return m[s] || 'Denumire'
    },

    areClient(s: string): boolean {
      return ['CONTACTE_NOI', 'INTALNIRI', 'DEMO', 'OPORTUNITATI_NOI',
              'INTALNIRI_PORTOFOLIU', 'UPSELL_CROSSSELL', 'OPORTUNITATI_PORTOFOLIU',
              'OFERTE_TRIMISE'].includes(s)
    },

    areContact(s: string): boolean {
      return ['CONTACTE_NOI', 'CLIENTI_CONTACTATI'].includes(s)
    },

    labelContact(s: string): string {
      return s === 'CONTACTE_NOI' ? 'Email' : 'Nr. activități'
    },

    areValoare(s: string): boolean {
      return ['OPORTUNITATI_NOI', 'OPORTUNITATI_PORTOFOLIU'].includes(s)
    },

    areStatus(s: string): boolean {
      return ['OPORTUNITATI_NOI', 'OPORTUNITATI_PORTOFOLIU'].includes(s)
    },

    navigheazaLaDetaliu(sursaDate: string, rowId: any) {
      if (['COMPANII_NOI', 'CLIENTI_CONTACTATI'].includes(sursaDate)) {
        this.$router.push({ path: '/crm/parteneri', query: { partenerId: String(rowId) } })
      } else if (sursaDate === 'CONTACTE_NOI') {
        this.$router.push({ path: '/crm/contacte', query: { contactId: String(rowId) } })
      } else if (['OPORTUNITATI_NOI', 'OPORTUNITATI_PORTOFOLIU', 'OFERTE_TRIMISE'].includes(sursaDate)) {
        this.$router.push({ path: '/crm/grid-oportunitati', query: { opportunityId: String(rowId) } })
      } else if (['INTALNIRI', 'DEMO', 'UPSELL_CROSSSELL', 'INTALNIRI_PORTOFOLIU'].includes(sursaDate)) {
        this.$router.push('/crm/activitati')
      }
    },

    formatData(val: string | null): string {
      if (!val) return '—'
      return new Date(val).toLocaleDateString('ro-RO')
    },

    formatValoare(val: number): string {
      return new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 }).format(val)
    },
  }
})
</script>

<style scoped>
.echipa-wrap { padding: 1.5rem; }

.filter-card { border-radius: 14px !important; padding: 0.875rem 1.25rem; }
.filter-row { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
.filter-group { display: flex; align-items: center; gap: 8px; flex: 1; }
.filter-select { max-width: 150px; }

.refresh-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 10px !important;
  padding: 0 16px !important;
  height: 34px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  background: white !important;
  color: #4a5568 !important;
  border: 1px solid #e2e8f0 !important;
  font-size: 0.8125rem !important;
}
.refresh-btn:hover {
  transform: translateY(-1px);
  background: rgba(255,87,34,0.04) !important;
  color: #ff5722 !important;
  border-color: rgba(255,87,34,0.3) !important;
}
.refresh-btn .v-icon { transition: transform 0.6s cubic-bezier(0.4,0,0.2,1) !important; }
.refresh-btn:hover .v-icon { transform: rotate(180deg); }

/* Summary */
.summary-card { border-radius: 14px !important; }
.summary-inner { display: flex; align-items: center; gap: 1rem; padding: 1rem 1.25rem; }
.summary-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.summary-value { font-size: 1.5rem; font-weight: 800; line-height: 1; color: #1a202c; }
.summary-label { font-size: 0.7rem; color: #a0aec0; text-transform: uppercase; letter-spacing: 0.04em; margin-top: 3px; }

/* Matrix */
.matrix-card { border-radius: 16px !important; overflow: hidden; }
.matrix-title {
  display: flex; align-items: center;
  padding: 1rem 1.5rem;
  font-weight: 600; font-size: 0.9rem;
  border-bottom: 1px solid #edf2f7; background: #fafafa;
}
.matrix-hint { font-size: 0.7rem; color: #a0aec0; font-weight: 400; }
.table-scroll { overflow-x: auto; }

.matrix-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.matrix-table thead th {
  background: #f7fafc; padding: 0.6rem 0.75rem;
  text-align: center; font-size: 0.7rem; font-weight: 700;
  color: #718096; text-transform: uppercase; letter-spacing: 0.04em;
  border-bottom: 2px solid #edf2f7; white-space: nowrap;
}
.matrix-table thead .agent-col { text-align: left; min-width: 140px; }
.matrix-table thead .medie-col { min-width: 80px; }
.matrix-table tbody td {
  padding: 0.5rem 0.75rem; border-bottom: 1px solid #edf2f7;
  text-align: center; vertical-align: middle;
}
.matrix-table tbody .agent-col { text-align: left; }

.agent-row { cursor: pointer; transition: background 0.15s; }
.agent-row:hover { background: #f7fafc; }
.agent-row--expanded { background: #fff8f5 !important; }

.agent-name-wrap { display: flex; align-items: center; }
.agent-name { font-weight: 600; color: #1a202c; font-size: 0.875rem; }
.expand-chevron { transition: transform 0.2s ease; flex-shrink: 0; }
.expand-chevron--open { transform: rotate(90deg); }

.cell-wrap { display: flex; align-items: baseline; justify-content: center; gap: 2px; font-size: 0.875rem; }
.cell-target { font-size: 0.75rem; color: #a0aec0; }
.cell-bar-wrap { display: flex; align-items: center; gap: 4px; margin-top: 3px; }
.cell-bar { flex: 1; height: 4px; background: #f0f0f0; border-radius: 2px; overflow: hidden; min-width: 40px; }
.cell-fill { height: 100%; border-radius: 2px; transition: width 0.4s ease; }
.cell-pct { font-size: 0.7rem; font-weight: 700; min-width: 32px; text-align: right; }
.medie-col { text-align: center !important; }

/* Expand row */
.detalii-row > td { padding: 0 !important; border-bottom: 2px solid #e2e8f0 !important; }
.detalii-cell { background: #f8fafc; }
.detalii-content { padding: 1rem 1.5rem 1.25rem; }

/* KPI detalii grid */
.kpi-detalii-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  gap: 0.875rem;
}

.kpi-detalii-block {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  background: white;
}

.kpi-block-header {
  padding: 0.6rem 0.875rem;
  background: #f7fafc;
  border-bottom: 1px solid #edf2f7;
}

.kpi-block-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #2d3748;
  display: block;
  margin-bottom: 4px;
}

.kpi-block-progress-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.kpi-block-pct { font-size: 0.75rem; font-weight: 700; }

.kpi-block-bar { height: 4px; background: #edf2f7; border-radius: 2px; overflow: hidden; }
.kpi-block-fill { height: 100%; border-radius: 2px; transition: width 0.4s ease; }

.kpi-block-body { padding: 0; }

.kpi-block-empty {
  display: flex; align-items: center; justify-content: center;
  padding: 0.75rem; font-size: 0.75rem; color: #a0aec0; gap: 6px;
}

.mini-scroll { max-height: 200px; overflow-y: auto; }

.mini-table { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
.mini-table thead th {
  background: #f7fafc; padding: 0.35rem 0.6rem;
  text-align: left; font-size: 0.65rem; font-weight: 700;
  color: #718096; text-transform: uppercase; letter-spacing: 0.04em;
  border-bottom: 1px solid #edf2f7; white-space: nowrap;
  position: sticky; top: 0; z-index: 1;
}
.mini-table tbody td {
  padding: 0.35rem 0.6rem;
  border-bottom: 1px solid #f7fafc;
  color: #2d3748; vertical-align: middle;
}
.mini-table tbody tr:last-child td { border-bottom: none; }
.mini-table tbody tr:hover td { background: #f7fafc; }

.mini-col-main { font-weight: 500; max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mini-col-client { color: #4a5568; max-width: 110px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mini-col-info { color: #718096; }
.mini-col-val { text-align: right; font-weight: 600; white-space: nowrap; }
.mini-col-data { color: #a0aec0; white-space: nowrap; }

.mini-row-link { cursor: pointer; }
.mini-row-link:hover td { background: #edf2f7 !important; }
</style>
